// =======================================================
// Feedbee Unified Analytics & Event Tracking Engine
// Supports:
// 1. Firebase Analytics / Google Analytics (G-2MJFKHFNF7)
// 2. Heap Analytics (Autocapture & Custom Events)
// 3. Plausible Analytics (Privacy-friendly Pageviews & Goals)
// =======================================================

const HEAP_APP_ID = process.env.REACT_APP_HEAP_ANALYTICS_KEY || '3903368766';
const FIREBASE_MEASUREMENT_ID = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-2MJFKHFNF7';

/**
 * Initializes Analytics SDKs (Firebase Analytics / gtag, Heap, Plausible).
 */
export const initAnalytics = () => {
    if (typeof window === 'undefined') return;

    // 1. Initialize Firebase Analytics (gtag.js)
    if (!window.gtag) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', FIREBASE_MEASUREMENT_ID, {
            send_page_view: true,
        });

        if (!document.querySelector(`script[src*="${FIREBASE_MEASUREMENT_ID}"]`)) {
            const gtagScript = document.createElement('script');
            gtagScript.async = true;
            gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${FIREBASE_MEASUREMENT_ID}`;
            document.head.appendChild(gtagScript);
        }
    }

    // 2. Initialize Heap Analytics
    if (!window.heap) {
        window.heapReadyCb = window.heapReadyCb || [];
        window.heap = window.heap || [];
        window.heap.load = function (e, t) {
            window.heap.envId = e;
            window.heap.clientConfig = t = t || {};
            window.heap.clientConfig.shouldFetchServerConfig = !1;
            const a = document.createElement('script');
            a.type = 'text/javascript';
            a.async = !0;
            a.src = 'https://cdn.heapanalytics.com/js/heap-' + e + '.js';
            const r = document.getElementsByTagName('script')[0];
            r.parentNode.insertBefore(a, r);
            const n = function (method) {
                return function () {
                    window.heap.push([method].concat(Array.prototype.slice.call(arguments, 0)));
                };
            };
            const methods = [
                'addEventProperties',
                'addUserProperties',
                'clearEventProperties',
                'identify',
                'resetIdentity',
                'removeEventProperty',
                'setEventProperties',
                'track',
                'unsetEventProperty',
            ];
            for (let o = 0; o < methods.length; o++) {
                window.heap[methods[o]] = n(methods[o]);
            }
        };

        if (HEAP_APP_ID) {
            window.heap.load(HEAP_APP_ID);
        }
    }
};

/**
 * Tracks a named event with optional key-value properties across Firebase, Heap and Plausible.
 * @param {string} eventName - Descriptive event name (e.g., 'page_created', 'feedback_submitted')
 * @param {object} properties - Contextual data payload
 */
export const trackEvent = (eventName, properties = {}) => {
    try {
        if (typeof window === 'undefined') return;

        // 1. Send to Firebase Analytics / Google Analytics
        if (typeof window.gtag === 'function') {
            window.gtag('event', eventName, properties);
        }

        // 2. Send to Heap Analytics
        if (window.heap && typeof window.heap.track === 'function') {
            window.heap.track(eventName, properties);
        }

        // 3. Send to Plausible Analytics
        if (window.plausible && typeof window.plausible === 'function') {
            window.plausible(eventName, { props: properties });
        }

        if (process.env.NODE_ENV !== 'production') {
            console.log(`[Feedbee Analytics] Tracked: "${eventName}"`, properties);
        }
    } catch (err) {
        console.warn('[Feedbee Analytics] Track event error:', err);
    }
};

/**
 * Identifies a logged-in user in Firebase and Heap with user traits.
 * @param {string} userId - User document ID or identifier
 * @param {object} traits - User attributes (email, plan, etc.)
 */
export const identifyUser = (userId, traits = {}) => {
    try {
        if (typeof window === 'undefined') return;

        // 1. Set user in Firebase Analytics / Google Analytics
        if (typeof window.gtag === 'function') {
            if (userId) {
                window.gtag('set', { user_id: String(userId) });
            }
            if (traits && Object.keys(traits).length > 0) {
                window.gtag('set', 'user_properties', traits);
            }
        }

        // 2. Set user in Heap
        if (window.heap) {
            if (userId && typeof window.heap.identify === 'function') {
                window.heap.identify(String(userId));
            }

            if (traits && Object.keys(traits).length > 0 && typeof window.heap.addUserProperties === 'function') {
                window.heap.addUserProperties(traits);
            }
        }
    } catch (err) {
        console.warn('[Feedbee Analytics] Identify user error:', err);
    }
};

/**
 * Resets user identity upon logout across Firebase and Heap.
 */
export const resetUser = () => {
    try {
        if (typeof window === 'undefined') return;

        // 1. Reset user in Firebase Analytics / Google Analytics
        if (typeof window.gtag === 'function') {
            window.gtag('set', { user_id: null });
        }

        // 2. Reset user in Heap
        if (window.heap && typeof window.heap.resetIdentity === 'function') {
            window.heap.resetIdentity();
        }
    } catch (err) {
        console.warn('[Feedbee Analytics] Reset user error:', err);
    }
};

/**
 * Tracks SPA route changes across Firebase, Plausible and Heap.
 * @param {string} pathname - Current route path
 */
export const trackPageView = (pathname) => {
    try {
        if (typeof window === 'undefined') return;

        const currentPath = pathname || window.location.pathname;

        // 1. Send page_view to Firebase Analytics / Google Analytics
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
                page_path: currentPath,
                page_location: window.location.href,
                page_title: document.title,
            });
        }

        // 2. Send pageview to Plausible
        if (window.plausible && typeof window.plausible === 'function') {
            window.plausible('pageview', { u: window.location.href });
        }
    } catch (err) {
        console.warn('[Feedbee Analytics] PageView error:', err);
    }
};

export default {
    initAnalytics,
    trackEvent,
    identifyUser,
    resetUser,
    trackPageView,
};
